import { useCallback, useEffect, useState } from "react";

import { GET_DEPARTMENT_LIST } from "../../config/Endpoints.js";
import type { SelectOption } from "../components/ui/select/select.tsx";
import { ApiError, getApi } from "../utils/api.ts";
import { accessToken } from "../utils/auth.ts";

function parseDepartmentOptions(payload: unknown): SelectOption[] {
  const rows =
    payload && typeof payload === "object" && "data" in payload
      ? (payload as { data?: unknown }).data
      : payload;
  if (!Array.isArray(rows)) return [];

  return rows.flatMap((row) => {
    if (!row || typeof row !== "object") return [];
    const item = row as Record<string, unknown>;
    const id = item.id ?? item.departmentId;
    const label = item.name ?? item.departmentName;
    return id != null && typeof label === "string" && label.trim()
      ? [{ value: String(id), label: label.trim() }]
      : [];
  });
}

export function useDepartmentOptions() {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getApi<unknown>(
        GET_DEPARTMENT_LIST,
        accessToken(),
        "json",
        signal,
      );
      setOptions(parseDepartmentOptions(data));
    } catch (cause) {
      if (signal?.aborted) return;
      setOptions([]);
      setError(
        cause instanceof ApiError
          ? cause.message
          : "Failed to load departments.",
      );
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void refetch(controller.signal);
    return () => controller.abort();
  }, [refetch]);

  return { options, loading, error, refetch };
}
